import { ArrowUpRight, GraduationCap } from "lucide-react";
import { useLang } from "@/i18n";
import { useDiscovery, HEADSHOT } from "@/components/Layout";
import { MaskedLines, Reveal, FadeIn, ParallaxImage, Kicker } from "@/components/motion";

const HALLWAY =
  "https://images.unsplash.com/photo-1641060272821-df59e2c0b5ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBvZmZpY2UlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4NzQ2OTI1NXww&ixlib=rb-4.1.0&q=85&w=1600";

export default function About() {
  const { t } = useLang();
  const open = useDiscovery();
  const a = t.about;

  return (
    <>
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20 grain" data-testid="about-hero">
        <div className="pointer-events-none absolute -right-40 top-0 h-[460px] w-[460px] rounded-full bg-sage-100/70 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <FadeIn delay={0.1}>
              <Kicker>{a.kicker}</Kicker>
            </FadeIn>
            <MaskedLines
              className="mt-9 font-serif text-4xl leading-[1.08] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
              delay={0.25}
              lines={["Alessia Zanella"]}
            />
            <Reveal delay={0.5}>
              <p className="mt-4 font-serif text-xl italic tracking-tight text-sage-700 sm:text-2xl" data-testid="about-subline">
                {a.title}
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-navy-900/70 sm:text-lg" data-testid="about-intro">
                {a.intro}
              </p>
            </Reveal>
          </div>
          <div className="relative lg:col-span-5">
            <FadeIn delay={0.45} className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-gold/50" />
              <ParallaxImage
                src={HEADSHOT}
                alt="Alessia Zanella, Founder di Eclenia"
                testId="about-portrait"
                className="relative aspect-[4/5] w-full shadow-2xl"
                speed={0.08}
              />
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="about-journey">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          {a.chapters.map((c, i) => (
            <Reveal key={i} className="group grid gap-6 border-t border-navy-900/10 py-12 sm:grid-cols-12 sm:py-16">
              <div className="sm:col-span-3">
                <span className="font-mono text-sm text-gold-deep" data-testid={`about-chapter-num-${i}`}>{c.n}</span>
                <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-navy-900/45">{c.period}</p>
              </div>
              <div className="sm:col-span-9">
                <h2 className="font-serif text-2xl tracking-tight text-navy-900 transition-colors duration-500 group-hover:text-sage-700 sm:text-3xl" data-testid={`about-chapter-title-${i}`}>
                  {c.title}
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy-900/70">{c.body}</p>
                <p className="mt-5 inline-block border border-sage-200 bg-sage-50 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-sage-700" data-testid={`about-chapter-skill-${i}`}>
                  {c.skill}
                </p>
              </div>
            </Reveal>
          ))}
          <Reveal className="border-t border-navy-900/10 pt-12">
            <p className="max-w-3xl border-l-2 border-gold pl-6 font-serif text-xl italic leading-relaxed text-navy-900 sm:text-2xl" data-testid="founder-network-text">
              {a.network}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy-900 py-20 text-cream sm:py-24 grain relative" data-testid="about-credentials">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker light>{a.credentials.kicker}</Kicker>
              <h2 className="mt-4 font-serif text-2xl tracking-tight sm:text-3xl lg:text-4xl">{a.credentials.title}</h2>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-cream/60">{a.credentials.note}</p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8">
              <ParallaxImage
                src={HALLWAY}
                alt="Modern corporate architecture"
                testId="about-credentials-image"
                className="aspect-[4/3] w-full"
                speed={0.1}
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-px border border-cream/10 bg-cream/10">
              {a.credentials.items.map((item, i) => (
                <Reveal key={i} delay={i * 0.07} className="flex items-center gap-4 bg-navy-900 p-6 transition-colors duration-500 hover:bg-navy-800">
                  <GraduationCap size={18} className="shrink-0 text-gold" />
                  <p className="text-sm font-medium text-cream/90" data-testid={`credential-item-${i}`}>{item}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3} className="mt-10">
              <button
                onClick={open}
                data-testid="about-cta-btn"
                className="group flex items-center gap-2.5 border border-gold/60 px-7 py-4 text-sm font-medium tracking-wide text-gold-light transition-all duration-300 hover:bg-gold hover:text-navy-900"
              >
                {a.cta}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative border-t border-gold/25 bg-navy-950 py-20 text-cream sm:py-28 grain" data-testid="eclenia-section">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-sage-700/20 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <Kicker light>{a.eclenia.title}</Kicker>
            <h2 className="mt-5 font-serif text-3xl italic tracking-tight text-gold-light sm:text-4xl lg:text-5xl" data-testid="eclenia-subtitle">
              {a.eclenia.subtitle}
            </h2>
            <div className="mt-9 space-y-6 text-base leading-relaxed text-cream/70 sm:text-lg" data-testid="eclenia-body">
              <p>{a.eclenia.p1}</p>
              <p>{a.eclenia.p2}</p>
              <p>{a.eclenia.p3}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
